import { DateTime } from 'luxon'

/**
 * Returns a Date object representing the same date and time in a specified timezone.
 *
 * This function does not convert the datetime to the target timezone but sets the same
 * datetime in the different timezone. For example, if you select 1st January 8:00 AM in
 * Tokyo timezone and pass this datetime to the function with Bratislava timezone, it will
 * return 1st January 8:00 AM in Bratislava's time.
 *
 * Note: This function will strip every smaller unit than minutes.
 *
 * @param {Date} [date=new Date()] - The date object to be used. Defaults to the current date and time.
 * @param {string} [zone='UTC'] - The target timezone. Defaults to 'UTC'.
 * @returns {Date} - A Date object representing the same date and time in the specified timezone.
 */
export const getDateTimeInTimezone = (date = new Date(), zone = 'UTC'): Date => {
  return DateTime.fromObject(
    {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
      hour: date.getHours(),
      minute: date.getMinutes(),
    },
    { zone },
  ).toJSDate()
}

/**
 * Returns a Date object representing the same date in a specified timezone, with the time stripped.
 *
 * This function sets the same date in the different timezone but strips the time part.
 * For example, if you select 1st January in Tokyo timezone and pass this date to the function
 * with Bratislava timezone, it will return 1st January in Bratislava's time with time set to 00:00.
 *
 * @param {Date} [date=new Date()] - The date object to be used. Defaults to the current date.
 * @param {string} [zone='UTC'] - The target timezone. Defaults to 'UTC'.
 * @returns {Date} - A Date object representing the same date in the specified timezone with time set to 00:00.
 */
export const getDateInTimezone = (
  date = new Date(),
  zone = 'UTC',
  timeOfTheDay: 'start' | 'end' | 'noon' = 'start',
): Date => {
  const dateTime = DateTime.fromObject(
    {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    },
    { zone },
  )
  if (timeOfTheDay === 'noon') {
    return dateTime.startOf('day').plus({ hours: 12 }).toJSDate()
  }
  if (timeOfTheDay === 'end') {
    return dateTime.endOf('day').toJSDate()
  }
  return dateTime.startOf('day').toJSDate()
}

/**
 * Converts a UTC date to a specified timezone and returns a Date object representing the same time
 * in the local system timezone.
 *
 * This function is useful for displaying dates on a calendar in the entity's timezone rather than the client's timezone.
 *
 * Only does conversion from UTC into specified timezone. Then fakes that specific time in whatever is the local system timezone.
 * Ex: This is to show date on calednar in the entity timezone not client timezone.
 *
 * @param {string | Date} date - The date to be converted. Can be a string or a Date object.
 * @param {string} timezone - The target timezone.
 * @returns {Date} - A Date object representing the same time in the local system timezone.
 */
export const getUTCintoTimezone = (date: string | Date, timezone: string): Date => {
  const browserTimezone = DateTime.local().zoneName
  let dateIsoString = ''
  if (typeof date === 'string') {
    dateIsoString = date
  }
  if (date instanceof Date) {
    dateIsoString = date.toISOString()
  }

  const inDesiredTimezone = DateTime.fromISO(dateIsoString, { zone: 'UTC' }).setZone(timezone)
  //   Create fake object with same time in whatever is the local system timezone.
  return DateTime.fromObject(
    {
      year: inDesiredTimezone.year,
      month: inDesiredTimezone.month,
      day: inDesiredTimezone.day,
      hour: inDesiredTimezone.hour,
      minute: inDesiredTimezone.minute,
    },
    { zone: browserTimezone },
  ).toJSDate()
}

/**
 * The 42 days a month calendar shows: six Monday-first weeks, starting on the Monday on or before the 1st of `month`'s
 * month, each at local midnight. Any day of the month gives the same grid. Six rows always, so a calendar's height
 * never changes between months. Built with Luxon's calendar arithmetic, so a DST change inside the grid still lands
 * every day on midnight.
 */
export const monthGrid = (month: Date): Date[] => {
  const first = DateTime.fromJSDate(month).startOf('month').startOf('week')
  return Array.from({ length: 42 }, (_, day) => first.plus({ days: day }).toJSDate())
}

/**
 * A person's age in whole years on `today`, or `null` when the birth date is unknown.
 *
 * The one definition of age - the response types declare `age: number | null`, and
 * `ManagedEventView` computes the same thing in SQL (`EXTRACT(YEAR FROM AGE(...))`), so a change
 * here must be mirrored there. `null` rather than `0` for an unknown birth date: a customer-group
 * age range must not treat "unknown" as a newborn.
 *
 * Accepts a string because that is what actually arrives: Postgres `date` columns come back from
 * the driver as `'YYYY-MM-DD'` even though the entities type them as `Date`. Birth dates are
 * calendar dates, so both forms are read as a UTC calendar day.
 */
export const userAge = (
  birthDate: Date | string | null | undefined,
  today: DateTime = DateTime.utc(),
): number | null => {
  if (!birthDate) return null
  const born =
    typeof birthDate === 'string'
      ? DateTime.fromISO(birthDate, { zone: 'utc' })
      : DateTime.fromJSDate(birthDate, { zone: 'utc' })
  if (!born.isValid) return null
  const years = Math.floor(today.startOf('day').diff(born.startOf('day'), 'years').years)
  return years < 0 ? null : years
}

/**
 * A calendar date (a birth date: `'YYYY-MM-DD'` from a Postgres `date` column) as a date picker holds it: local
 * midnight of that day, so the picker shows the same day in every timezone. `new Date('1992-04-12')` would be UTC
 * midnight, which a browser west of UTC shows as the 11th.
 */
export const fromCalendarDate = (value: Date | string): Date => {
  const day =
    typeof value === 'string'
      ? DateTime.fromISO(value.slice(0, 10), { zone: 'utc' })
      : DateTime.fromJSDate(value, { zone: 'utc' })
  return new Date(day.year, day.month - 1, day.day)
}

/**
 * The day a date picker's `Date` (local midnight) stands for, as `'YYYY-MM-DD'`: what a calendar-date field sends. Its
 * `toISOString()` would be the previous day for any browser east of UTC.
 */
export const toCalendarDate = (date: Date): string => DateTime.fromJSDate(date).toISODate() ?? ''

/**
 * A reservation's time range as one human line, in the premise's zone: "Štvrtok 15. októbra 2026 · 10:00 – 11:00".
 * A range that ends on another calendar day (in that zone) spells both ends out instead: "15. 10. 2026 22:00 – 16. 10.
 * 2026 2:00". For e-mails and other text built on the backend, where the reader's browser zone is unknown.
 */
export const formatDateRangeInZone = (
  start: Date | string,
  end: Date | string,
  zone: string,
  locale = 'sk',
): string => {
  const from = DateTime.fromJSDate(new Date(start)).setZone(zone).setLocale(locale)
  const to = DateTime.fromJSDate(new Date(end)).setZone(zone).setLocale(locale)

  if (!from.hasSame(to, 'day')) {
    return `${from.toFormat('d. M. yyyy H:mm')} – ${to.toFormat('d. M. yyyy H:mm')}`
  }

  return `${capitalisedDay(from)} · ${from.toFormat('H:mm')} – ${to.toFormat('H:mm')}`
}

const capitalisedDay = (date: DateTime): string => {
  const day = date.toFormat('cccc d. MMMM yyyy')
  return `${day.charAt(0).toUpperCase()}${day.slice(1)}`
}

/** A day alone, in the premise's zone: "Štvrtok 15. októbra 2026" - an all-day event in an e-mail. */
export const formatDayInZone = (date: Date | string, zone: string, locale = 'sk'): string =>
  capitalisedDay(DateTime.fromJSDate(new Date(date)).setZone(zone).setLocale(locale))

/**
 * One instant as a short date and time in the premise's zone: "15. 10. 2026 10:00". What the older e-mail variables
 * (`reservationStart`, `eventStart`, …) carry.
 */
export const formatDateTimeInZone = (date: Date | string, zone: string, locale = 'sk'): string =>
  DateTime.fromJSDate(new Date(date)).setZone(zone).setLocale(locale).toLocaleString(DateTime.DATETIME_SHORT)
